import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Mirrors EOY_REPORT_POLL_SCHEDULE in EOYReportAssistantPanel.js. Kept as a
// separate copy (not imported) so this hook has no dependency on the EOY
// panel's internals.
const REPORT_POLL_SCHEDULE = [
  { durationMs: 30000, intervalMs: 10000 },
  { durationMs: 120000, intervalMs: 20000 },
  { durationMs: 80000, intervalMs: 40000 },
  { durationMs: 180000, intervalMs: 60000 },
];

function getNextPollInterval(elapsedMs) {
  let windowEndMs = 0;

  for (const scheduleWindow of REPORT_POLL_SCHEDULE) {
    windowEndMs += scheduleWindow.durationMs;
    if (elapsedMs < windowEndMs) {
      return Math.min(scheduleWindow.intervalMs, windowEndMs - elapsedMs);
    }
  }

  return null;
}

function clearPollTimer(timerRef) {
  if (!timerRef.current) return;
  window.clearTimeout(timerRef.current);
  timerRef.current = null;
}

function waitForPollInterval(timerRef, intervalMs) {
  return new Promise((resolve) => {
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      resolve();
    }, intervalMs);
  });
}

export function getReportErrorMessage(result) {
  if (!result) return "";
  if (typeof result === "string") return result;

  return (
    result.error?.message ||
    (typeof result.error === "string" ? result.error : "") ||
    result.response?.data?.error?.message ||
    result.message ||
    ""
  );
}

export function reportHasContent(report) {
  if (typeof report?.report === "string" && report.report.trim()) return true;

  const sections = report?.report_sections;
  if (
    sections &&
    typeof sections === "object" &&
    Object.values(sections).some(
      (value) => typeof value === "string" && value.trim()
    )
  ) {
    return true;
  }

  return Boolean(
    (typeof report?.edited_report === "string" && report.edited_report.trim()) ||
      (typeof report?.learning_overview === "string" &&
      report.learning_overview.trim()) ||
      (typeof report?.staff_notes_summary === "string" &&
        report.staff_notes_summary.trim())
  );
}

export function isReportTerminal(result) {
  return Boolean(
    (result?.success === true && reportHasContent(result)) ||
      (result?.success === false && getReportErrorMessage(result))
  );
}

function isUsableSavedReport(report) {
  return Boolean(report?.has_report && reportHasContent(report));
}

// A report is "done" (student can be hidden from the picker) once it has
// succeeded and isn't stale. A stale-but-present report (can_regenerate)
// stays selectable so the teacher can act on it via the regenerate button.
function isReportDone(report) {
  return Boolean(
    report?.success &&
      report?.has_report &&
      reportHasContent(report) &&
      !report?.can_regenerate
  );
}

function mergeReportsByStudentId(currentReports, nextReports) {
  const mergedByStudentId = new Map();

  [...currentReports, ...nextReports].forEach((report) => {
    if (!report?.student_id) return;
    mergedByStudentId.set(report.student_id, report);
  });

  return Array.from(mergedByStudentId.values());
}

function hasTerminalReportForStudent(reportsToCheck, studentId) {
  return reportsToCheck.some(
    (report) => report.student_id === studentId && isReportTerminal(report)
  );
}

function getPendingStudentIds(studentIds, reportsToCheck) {
  return studentIds.filter(
    (studentId) => !hasTerminalReportForStudent(reportsToCheck, studentId)
  );
}

function getTerminalReportCount(reportsToCheck, studentIds) {
  const studentIdSet = new Set(studentIds);

  return reportsToCheck.filter(
    (report) => studentIdSet.has(report.student_id) && isReportTerminal(report)
  ).length;
}

function buildFailedReport(studentId, displayName, message) {
  return {
    student_id: studentId,
    display_name: displayName,
    success: false,
    has_report: false,
    error: { message },
    report: null,
  };
}

/**
 * Shared generate/poll/merge orchestration for the enhanced AI report types
 * (New Class Insights, Screening Point, PTM). Mirrors the generate-then-poll
 * pattern in EOYReportAssistantPanel.js — batch generation calls the AI
 * service server-side (up to 4 minutes, p-limit(10) concurrency) and
 * production infra can end the request before that completes, so this polls
 * the "reports/class" endpoint on an interval to pick up each student's
 * report as soon as the server caches it, independent of whether the
 * original generate request ever resolves.
 *
 * Unlike EOY, the enhanced-reports API already normalizes and correlates
 * responses by student_id server-side (see normalizeBatch/normalizeClass in
 * enhanced-reports.controller.ts), so this hook doesn't need EOY's
 * defensive field-name reshaping.
 */
function useEnhancedReportWorkflow({
  generateBatch,
  getClassReports,
  buildStudentPayload,
  extraFields,
}) {
  const [reports, setReports] = useState([]);
  const [reportsSource, setReportsSource] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [genError, setGenError] = useState(null);

  const pollRunRef = useRef(0);
  const pollTimerRef = useRef(null);
  const reportsRef = useRef([]);

  useEffect(() => {
    reportsRef.current = reports;
  }, [reports]);

  const doneStudentIds = useMemo(
    () => reports.filter(isReportDone).map((report) => report.student_id),
    [reports]
  );

  const startPollRun = useCallback(() => {
    const runId = pollRunRef.current + 1;
    pollRunRef.current = runId;
    clearPollTimer(pollTimerRef);
    return runId;
  }, []);

  const resetReports = useCallback(() => {
    startPollRun();
    setReports([]);
    setReportsSource(null);
    setGenError(null);
    setGenerating(false);
    setProgress({ done: 0, total: 0 });
  }, [startPollRun]);

  useEffect(() => () => startPollRun(), [startPollRun]);

  const fetchClassReports = useCallback(
    async (studentIds) => {
      if (studentIds.length === 0) return [];
      const data = await getClassReports({ studentIds, ...extraFields });
      return (data?.reports || []).filter(Boolean);
    },
    [getClassReports, extraFields]
  );

  const loadSavedReports = useCallback(
    async (studentIds) => {
      setSavedReportsLoading(true);
      try {
        const classReports = await fetchClassReports(studentIds);
        const savedReports = classReports.filter(isUsableSavedReport);
        if (savedReports.length > 0) {
          setReports(savedReports);
          setReportsSource("saved");
        }
        return savedReports;
      } catch (err) {
        console.warn("Could not load saved enhanced reports:", err);
        return [];
      } finally {
        setSavedReportsLoading(false);
      }
    },
    [fetchClassReports]
  );

  const generateForStudents = useCallback(
    async (studentsToGenerate) => {
      if (studentsToGenerate.length === 0) return;

      const pollRunId = startPollRun();
      setGenerating(true);
      setGenError(null);
      setReportsSource("generated");

      const studentIds = studentsToGenerate.map((student) => student.student_id);
      const displayNameById = new Map(
        studentsToGenerate.map((student) => [student.student_id, student.display_name])
      );

      let collected = reportsRef.current;
      setProgress({ done: 0, total: studentIds.length });

      const updateCollected = (nextReports) => {
        if (nextReports.length === 0) return;
        collected = mergeReportsByStudentId(collected, nextReports);
        reportsRef.current = collected;
        setReports(collected);
        setProgress({
          done: getTerminalReportCount(collected, studentIds),
          total: studentIds.length,
        });
      };

      let generationRequestFailed = false;

      generateBatch({
        students: studentsToGenerate.map((student) =>
          buildStudentPayload(student.student_id)
        ),
        // Generation is only reachable from the UI after the teacher has
        // ticked the AI-generated-content disclaimer, so acknowledgement is
        // implicit by the time this fires.
        acknowledged: true,
      })
        .then((data) => {
          if (pollRunRef.current !== pollRunId) return;
          updateCollected((data?.results || []).filter(isReportTerminal));
        })
        .catch((err) => {
          if (pollRunRef.current !== pollRunId) return;
          console.error("Batch report generation failed:", err);
          generationRequestFailed = true;
          const message =
            getReportErrorMessage(err) || "Request failed. Please try again.";
          const failedStudentIds = getPendingStudentIds(studentIds, collected);
          updateCollected(
            failedStudentIds.map((studentId) =>
              buildFailedReport(studentId, displayNameById.get(studentId), message)
            )
          );
          setGenerating(false);
        });

      let pendingStudentIds = getPendingStudentIds(studentIds, collected);
      let pollElapsedMs = 0;

      while (pendingStudentIds.length > 0) {
        if (generationRequestFailed) break;

        const pollIntervalMs = getNextPollInterval(pollElapsedMs);
        if (pollIntervalMs === null) break;

        await waitForPollInterval(pollTimerRef, pollIntervalMs);
        pollElapsedMs += pollIntervalMs;
        if (pollRunRef.current !== pollRunId) return;
        if (generationRequestFailed) break;

        try {
          const classReports = await fetchClassReports(pendingStudentIds);
          if (pollRunRef.current !== pollRunId) return;
          updateCollected(classReports.filter(isReportTerminal));
        } catch (err) {
          console.warn("Could not poll enhanced report status:", err);
        }

        pendingStudentIds = getPendingStudentIds(pendingStudentIds, collected);
      }

      if (pollRunRef.current !== pollRunId) return;

      if (pendingStudentIds.length > 0 && !generationRequestFailed) {
        updateCollected(
          pendingStudentIds.map((studentId) =>
            buildFailedReport(
              studentId,
              displayNameById.get(studentId),
              "No report was returned for this student. Please try regenerating this report."
            )
          )
        );
      }

      setGenerating(false);
    },
    [generateBatch, fetchClassReports, buildStudentPayload, startPollRun]
  );

  const generate = useCallback(
    async (selectedStudents) => {
      const pollRunId = startPollRun();
      setGenerating(true);
      setGenError(null);

      const studentIds = selectedStudents.map((student) => student.student_id);

      let idsToGenerate = studentIds;
      try {
        const savedReports = await fetchClassReports(studentIds);
        if (pollRunRef.current !== pollRunId) return;

        const alreadyDone = savedReports.filter(isReportDone);
        if (alreadyDone.length > 0) {
          const merged = mergeReportsByStudentId(reportsRef.current, alreadyDone);
          reportsRef.current = merged;
          setReports(merged);
          setReportsSource("saved");
          const doneIds = new Set(alreadyDone.map((report) => report.student_id));
          idsToGenerate = studentIds.filter((studentId) => !doneIds.has(studentId));
        }
      } catch (err) {
        if (pollRunRef.current !== pollRunId) return;
        console.error("Could not check for saved enhanced reports before generation:", err);
        setGenError(
          "Could not check for saved reports, so no new reports were generated. Please try again."
        );
        setGenerating(false);
        return;
      }

      if (idsToGenerate.length === 0) {
        setGenerating(false);
        return;
      }

      const studentsToGenerate = selectedStudents.filter((student) =>
        idsToGenerate.includes(student.student_id)
      );

      await generateForStudents(studentsToGenerate);
    },
    [fetchClassReports, generateForStudents, startPollRun]
  );

  const regenerate = useCallback(
    (report) => {
      if (!report?.student_id) return Promise.resolve();
      return generateForStudents([
        { student_id: report.student_id, display_name: report.display_name },
      ]);
    },
    [generateForStudents]
  );

  return {
    reports,
    reportsSource,
    generating,
    savedReportsLoading,
    progress,
    genError,
    doneStudentIds,
    loadSavedReports,
    generate,
    regenerate,
    resetReports,
  };
}

export default useEnhancedReportWorkflow;
